import { useEffect, useLayoutEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { Inbox, Monitor, Send, Smartphone, X } from 'lucide-react';
import {
  DEFAULT_FEED_STYLE,
  expandProductFeeds,
  expandSharedBlocks,
  type CatalogProduct,
  type FeedStyle,
} from '@nopain-newsletter/core/blocks';
import { useFeedPreview } from '../../lib/useCatalog';
import { blockToHtml, escapeHtml, type Block } from '../../lib/blocks';
import { sharedBlockMap, useSharedBlocks } from '../../lib/useSharedBlocks';
import { htmlPreviewDocument } from '../html-template/previewDocument';
import { SegmentedControl } from './SegmentedControl';
import { prefersReducedMotion } from './hooks';
import { fillPreviewMergeFields } from '../../lib/previewMerge';

export type PreviewDevice = 'desktop' | 'mobile';

/**
 * Static shell for the live preview frame. The frame is sandboxed without
 * scripts; `allow-same-origin` only lets this page write the escaped block
 * HTML into `#pv-root` and toggle the hover/flash outline classes. Links are
 * inert (pointer-events: none), so nothing in the preview can navigate.
 */
const SHELL = `<!doctype html><html><head><meta charset="utf-8"><style>
html,body{margin:0;background:#fff}
a{pointer-events:none;cursor:default}
img{max-width:100%}
.wrap{max-width:600px;margin:0 auto;padding:24px}
body.m .wrap{padding:16px}
.em-b{position:relative;outline:2px dashed transparent;outline-offset:3px;border-radius:6px;transition:outline-color .15s}
.em-b.hl{outline-color:rgba(84,87,214,.55)}
.em-b.flash{animation:flash 1s cubic-bezier(.22,1,.36,1)}
@keyframes flash{0%{background:rgba(84,87,214,.12)}100%{background:transparent}}
.noalt{position:absolute;left:8px;top:8px;z-index:1;font:500 11px/1.4 system-ui,sans-serif;background:#fef3c7;color:#92400e;padding:3px 7px;border-radius:6px}
@media (prefers-reduced-motion: reduce){.em-b{transition:none}.em-b.flash{animation:none}}
</style></head><body><div id="pv-root"></div></body></html>`;

/** The email body as it is sent (`blockToHtml`), each block wrapped so the editor can outline it. */
export function previewBodyHtml(
  blocks: Block[],
  shared: ReadonlyMap<string, string> = new Map(),
  feeds: { products: readonly CatalogProduct[]; style: FeedStyle } = {
    products: [],
    style: DEFAULT_FEED_STYLE,
  },
): string {
  const inner = blocks
    .map((b) => {
      const badge =
        b.type === 'image' && !b.alt.trim() ? '<span class="noalt">No alt text</span>' : '';
      return `<div class="em-b" data-pv="${escapeHtml(b.id)}">${badge}${fillPreviewMergeFields(expandProductFeeds(expandSharedBlocks(blockToHtml(b), shared).html, feeds.products, feeds.style).html)}</div>`;
    })
    .join('');
  return `<div class="wrap">${inner}</div>`;
}

/** First line of text an inbox would show next to the subject. */
export function inboxSnippet(blocks: Block[], editorMode: string, html: string): string {
  if (editorMode === 'html') {
    return html
      .replace(/<(style|script)[\s\S]*?<\/\1>/gi, ' ')
      .replace(/<[^>]+>/g, ' ')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 160);
  }
  const first = blocks.find((b) => b.type === 'text' && b.text.trim());
  return first && first.type === 'text' ? first.text.replace(/\s+/g, ' ').trim().slice(0, 160) : '';
}

function BlocksFrame({
  blocks,
  device,
  highlightId,
  flash,
  scroller,
}: {
  blocks: Block[];
  device: PreviewDevice;
  highlightId: string | null;
  flash: { id: string; n: number } | null;
  scroller: RefObject<HTMLDivElement | null>;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);
  const { blocks: sharedBlocks } = useSharedBlocks();
  const sharedMap = useMemo(() => sharedBlockMap(sharedBlocks), [sharedBlocks]);
  const feedPreview = useFeedPreview();

  const root = () => frame.current?.contentDocument?.getElementById('pv-root') ?? null;
  const measure = () => {
    const f = frame.current;
    const r = f?.contentDocument?.getElementById('pv-root');
    if (!f || !r) return;
    f.style.height = `${Math.max(120, Math.ceil(r.getBoundingClientRect().height))}px`;
  };

  // The srcdoc may have loaded before React attached onLoad (fast paths).
  useEffect(() => {
    if (root()) setReady(true);
  }, []);

  useLayoutEffect(() => {
    const r = root();
    if (!ready || !r) return;
    r.innerHTML = previewBodyHtml(blocks, sharedMap, feedPreview);
    r.ownerDocument.body.classList.toggle('m', device === 'mobile');
    r.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', measure, { once: true });
    });
    measure();
  }, [ready, blocks, device, sharedMap, feedPreview]);

  useEffect(() => {
    const win = frame.current?.contentWindow;
    if (!ready || !win) return;
    win.addEventListener('resize', measure);
    return () => win.removeEventListener('resize', measure);
  }, [ready]);

  useEffect(() => {
    const r = root();
    if (!ready || !r) return;
    r.querySelectorAll('.em-b.hl').forEach((el) => el.classList.remove('hl'));
    if (highlightId) {
      r.querySelector(`[data-pv="${CSS.escape(highlightId)}"]`)?.classList.add('hl');
    }
  }, [ready, highlightId, blocks]);

  useEffect(() => {
    const r = root();
    const f = frame.current;
    const sc = scroller.current;
    if (!ready || !r || !f || !flash) return;
    const el = r.querySelector<HTMLElement>(`[data-pv="${CSS.escape(flash.id)}"]`);
    if (!el) return;
    if (sc) {
      const top = f.offsetTop + el.offsetTop;
      const bottom = top + el.offsetHeight;
      if (top < sc.scrollTop || bottom > sc.scrollTop + sc.clientHeight) {
        sc.scrollTo({
          top: Math.max(0, top - 48),
          behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        });
      }
    }
    if (prefersReducedMotion()) return;
    el.classList.remove('flash');
    void el.offsetWidth;
    el.classList.add('flash');
  }, [ready, flash, scroller]);

  return (
    <iframe
      ref={frame}
      title="Email preview"
      srcDoc={SHELL}
      sandbox="allow-same-origin"
      tabIndex={-1}
      onLoad={() => setReady(true)}
    />
  );
}

export interface EmailPreviewProps {
  blocks: Block[];
  editorMode: 'loading' | 'blocks' | 'html';
  /** The campaign's HTML in `html` mode (shown in a sandboxed frame); ignored in block mode. */
  html: string;
  subject: string;
  subjectB: string;
  /**
   * K6: content B (HTML) of the A/B test. Non-empty = the B view shows it
   * instead of content A (and subject line A when subject B is empty).
   */
  htmlB?: string;
  /** Preview text; shared by both subject lines. Empty = the inbox shows the first line of the email. */
  preheader: string;
  abEnabled: boolean;
  fromName: string | null;
  device: PreviewDevice;
  onDeviceChange: (d: PreviewDevice) => void;
  canSendTest: boolean;
  testPending: boolean;
  onSendTest: () => void;
  highlightId: string | null;
  flash: { id: string; n: number } | null;
  /** Bumped on every subject edit; shows the typing caret for a moment. */
  typingKey: number;
  mockMode: boolean;
  isMobile: boolean;
  sheetOpen: boolean;
  onCloseSheet: () => void;
  closeRef: RefObject<HTMLButtonElement | null>;
}

/**
 * Right-hand live preview: an inbox row (sender, subject, first line) above
 * the email itself, at desktop or phone width. On phones it becomes a
 * bottom sheet (a modal dialog) that can be dragged down to close.
 */
export function EmailPreview(p: EmailPreviewProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const aside = useRef<HTMLElement>(null);
  const [abView, setAbView] = useState<'A' | 'B'>('A');
  const [typing, setTyping] = useState(false);
  const firstTyping = useRef(true);

  useEffect(() => {
    if (firstTyping.current) {
      firstTyping.current = false;
      return;
    }
    if (prefersReducedMotion()) return;
    setTyping(true);
    const t = window.setTimeout(() => setTyping(false), 900);
    return () => window.clearTimeout(t);
  }, [p.typingKey]);

  // Vaul-style drag on the grab handle; past 110px it closes.
  const drag = useRef<{ y0: number; dy: number } | null>(null);
  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    drag.current = { y0: e.clientY, dy: 0 };
    e.currentTarget.setPointerCapture(e.pointerId);
    if (aside.current) aside.current.style.transition = 'none';
  };
  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!drag.current || !aside.current) return;
    drag.current.dy = Math.max(0, e.clientY - drag.current.y0);
    aside.current.style.transform = `translateY(${drag.current.dy}px)`;
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !aside.current) return;
    aside.current.style.transition = '';
    aside.current.style.transform = '';
    if (d.dy > 110) p.onCloseSheet();
  };

  const showB = p.abEnabled && abView === 'B';
  const hasContentB = (p.htmlB ?? '').trim() !== '';
  const showContentB = showB && hasContentB;
  const subj = fillPreviewMergeFields(
    showB && (p.subjectB.trim() || !hasContentB) ? p.subjectB : p.subject,
  );
  // A preview text wins; without one an inbox falls back to the first line of the email.
  const snippet = fillPreviewMergeFields(
    p.preheader.replace(/\s+/g, ' ').trim() ||
      (showContentB
        ? inboxSnippet([], 'html', p.htmlB ?? '')
        : inboxSnippet(p.blocks, p.editorMode, p.html)),
  );
  const sheet = p.isMobile;
  const hiddenSheet = sheet && !p.sheetOpen;

  return (
    <>
      {sheet && p.sheetOpen ? (
        <div className="pf-scrim" onClick={p.onCloseSheet} aria-hidden="true" />
      ) : null}
      <aside
        ref={aside}
        className={`pf-preview${sheet && p.sheetOpen ? ' pf-sheet-open' : ''}`}
        aria-label={sheet ? 'Preview' : 'Live preview'}
        role={sheet && p.sheetOpen ? 'dialog' : undefined}
        aria-modal={sheet && p.sheetOpen ? true : undefined}
        aria-hidden={hiddenSheet ? true : undefined}
        inert={hiddenSheet}
      >
        <div
          className="pf-grab"
          aria-hidden="true"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        />
        <div className="pf-pv-bar">
          <h2>Preview</h2>
          <span className="pf-live">
            <i />
            Updates as you type
          </span>
          <span className="pf-gap" />
          <SegmentedControl
            label="Preview width"
            className="pf-pvdev"
            value={p.device}
            onChange={p.onDeviceChange}
            options={[
              {
                value: 'desktop',
                label: (
                  <>
                    <Monitor aria-hidden="true" />
                    Desktop
                  </>
                ),
                ariaLabel: 'Desktop',
              },
              {
                value: 'mobile',
                label: (
                  <>
                    <Smartphone aria-hidden="true" />
                    Mobile
                  </>
                ),
                ariaLabel: 'Mobile',
              },
            ]}
          />
          {p.canSendTest ? (
            <button
              type="button"
              className="pf-btn"
              disabled={p.testPending}
              onClick={p.onSendTest}
            >
              <Send aria-hidden="true" />
              {p.testPending ? 'Sending…' : 'Send test'}
            </button>
          ) : null}
          <button
            ref={p.closeRef}
            type="button"
            className="pf-btn pf-btn-ghost pf-btn-icon pf-pv-close"
            aria-label="Close preview"
            onClick={p.onCloseSheet}
          >
            <X aria-hidden="true" />
          </button>
        </div>
        <div className="pf-pv-scroll" ref={scroller}>
          <div className="pf-stage" data-device={p.device}>
            <div className="pf-inbox">
              <div className="pf-inbox-cap">
                <Inbox aria-hidden="true" />
                Inbox
                {p.abEnabled ? (
                  <SegmentedControl
                    small
                    label={hasContentB ? 'Variant shown' : 'Subject line shown'}
                    value={abView}
                    onChange={setAbView}
                    options={[
                      {
                        value: 'A',
                        label: 'A',
                        ariaLabel: hasContentB ? 'Show variant A' : 'Show subject line A',
                      },
                      {
                        value: 'B',
                        label: 'B',
                        ariaLabel: hasContentB ? 'Show variant B' : 'Show subject line B',
                      },
                    ]}
                  />
                ) : null}
              </div>
              <div className="pf-ghostrow" aria-hidden="true">
                <span className="pf-av" />
                <span>
                  <span className="pf-ln" style={{ width: '28%' }} />
                  <span className="pf-ln" style={{ width: '72%' }} />
                </span>
              </div>
              <div className={`pf-mail${typing ? ' pf-typing' : ''}`} data-testid="inbox-row">
                <span className="pf-av" aria-hidden="true">
                  {(p.fromName?.trim()[0] ?? '?').toUpperCase()}
                </span>
                <span className={`pf-from${p.fromName ? '' : ' pf-empty'}`}>
                  {p.fromName || 'No sender yet'}
                </span>
                <span className="pf-when">Now</span>
                <span className="pf-line">
                  <span className={`pf-subj${subj.trim() ? '' : ' pf-empty'}`}>
                    {subj.trim() ? subj : 'No subject yet'}
                  </span>
                  <span className={`pf-pre${snippet ? '' : ' pf-empty'}`}>
                    {' – '}
                    {snippet || 'No preview text yet, inboxes show the first line of the email'}
                  </span>
                  <span className="pf-caret" aria-hidden="true" />
                </span>
              </div>
              <div className="pf-ghostrow" aria-hidden="true">
                <span className="pf-av" />
                <span>
                  <span className="pf-ln" style={{ width: '22%' }} />
                  <span className="pf-ln" style={{ width: '64%' }} />
                </span>
              </div>
            </div>
            <div className="pf-frame">
              {showContentB ? (
                <iframe
                  title="Content B"
                  srcDoc={htmlPreviewDocument(fillPreviewMergeFields(p.htmlB ?? ''))}
                  sandbox=""
                  style={{ height: 560 }}
                />
              ) : p.editorMode === 'html' ? (
                <iframe
                  title="Current content"
                  srcDoc={htmlPreviewDocument(fillPreviewMergeFields(p.html))}
                  sandbox=""
                  style={{ height: 560 }}
                />
              ) : p.editorMode === 'blocks' && p.blocks.length > 0 ? (
                <BlocksFrame
                  blocks={p.blocks}
                  device={p.device}
                  highlightId={p.highlightId}
                  flash={p.flash}
                  scroller={scroller}
                />
              ) : (
                <p className="pf-pv-empty">
                  {p.editorMode === 'loading'
                    ? 'Loading content…'
                    : 'Nothing to preview yet. Add a block in Content.'}
                </p>
              )}
            </div>
            <p className="pf-pv-note">
              {p.mockMode ? 'Sample data. ' : ''}Merge fields show sample values (first name: Anna).
              Links are not clickable in the preview.
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}
